import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import styled from "styled-components";
import AppText from "../../components/Core/AppText/AppText";
import Button, { DownloadButton } from "../../components/Core/Button/Button";
import TableLayout from "../../components/Layout/TableLayout";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import EmptyState from "../../components/Shared/EmptyState/EmptyState";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import SalaryBatchProcessingModal from "../../components/Shared/SalaryBatchProcessingModal/SalaryBatchProcessingModal";
import BatchWarningSummary from "../../components/Shared/BatchWarningSummary/BatchWarningSummary";
import BatchStalenessBanner from "../../components/Shared/BatchStalenessBanner/BatchStalenessBanner";
import ButtonNew from "../../components/Core/Button/ButtonNew";
import { RefreshCw } from "lucide-react";
import { SIDE_MENU_WIDTH } from "../../components/Shared/SideMenu/SideMenu";
import {
	SalaryBatchDetailsBreadcrumbs,
	SalaryBatchNewBreadcrumbs,
} from "../../configs/breadcrumbNavigation";
import { SALARY_BATCH_STATUS } from "../../configs/constants/SalaryBatchStatus";
import { routes } from "../../configs/constants/routes";
import { FEEDBACK_MESSAGE_TYPE } from "../../configs/feedbackMessages";
import { ApiError, ApiErrorResponse } from "../../models/ApiResponse";
import {
	ConsultantSalary,
	NewSalaryBatchRequest,
	SalaryBatch,
	SalaryBatchFilter,
} from "../../models/Salary";
import { useOrganisation } from "../../networking/organisation.hooks";
import {
	parseSalaryBatch,
	useCreateSalaryBatch,
	usePollSalaryBatch,
	useProcessSalaryBatch,
	useRefreshSalaryBatch,
	useRollbackSalaryBatch,
	useSalaryBatchStaleness,
	useSalaryExportFileUrl,
	useUpdateSalaryBatchConsultants,
} from "../../networking/salary.hooks";
import { ConfirmModalProps } from "../../stores/confirmModal.store";
import { wait } from "../../utils/promise.utils";
import { formatCurrencyString } from "../../utils/string.utils";
import SalaryBatchForm from "./SalaryBatchForm";
import SalaryDetailsModal from "./SalaryDetailsModal";
import { toast } from "../../utils/toast";
import { useConfirmModalStore } from "../../stores/confirmModal.store";

interface SalaryBatchDetailsParamProps {
	salaryBatchId?: string;
}

const SalaryBatchPage: React.FC = () => {
	const navigate = useNavigate();
	const { salaryBatchId } = useParams<
		keyof SalaryBatchDetailsParamProps
	>() as SalaryBatchDetailsParamProps;
	const { t } = useTranslation();
	const modalStore = useConfirmModalStore();

	const { mutateAsync: pollSalaryBatch, isLoading: isFetchingBatch } = usePollSalaryBatch();
	const { mutateAsync: createSalaryBatch, isLoading: isCreatingBatch } = useCreateSalaryBatch();
	const { mutateAsync: updateSalaryBatchConsultants } = useUpdateSalaryBatchConsultants();
	const { mutateAsync: processSalaryBatch } = useProcessSalaryBatch();
	const { mutateAsync: rollbackSalaryBatch } = useRollbackSalaryBatch();
	const { mutateAsync: refreshSalaryBatch } = useRefreshSalaryBatch();
	const { mutateAsync: getExportFileUrl } = useSalaryExportFileUrl();

	const { data: organisation } = useOrganisation();

	// The Hogia export produces a downloadable zip (person file + both salary file formats).
	const isHogiaExport = organisation?.salarySettings.exportType === "HOGIA";

	// The batch being previewed/edited. Page-local draft state (was the
	// salaryStore.salaryBatchToEdit observable): the page mutates it from
	// polling and update responses while the user includes/excludes consultants.
	const [salaryBatch, setSalaryBatch] = useState<SalaryBatch | undefined>();
	const isEditable = salaryBatch?.status === SALARY_BATCH_STATUS.PREVIEW;
	const isLoadingBatch = isFetchingBatch || isCreatingBatch;

	const [consultantIdList, setConsultantIdList] = useState<string[]>([]);
	const [showConsultantDetails, setShowConsultantDetails] = useState<
		ConsultantSalary | undefined
	>();
	const [processingSalaryBatch, setProcessingSalaryBatch] = useState<SalaryBatch>();
	const [processingError, setProcessingError] = useState<ApiError>();
	const [isProcessing, setProcessing] = useState(false);
	const [showProcessingResult, setShowProcessingResult] = useState(false);
	const [isPollingPreview, setIsPollingPreview] = useState(false);
	const [isRefreshing, setRefreshing] = useState(false);

	// Only a PREVIEW draft can meaningfully drift; other statuses always
	// answer "not stale" anyway
	const { staleness } = useSalaryBatchStaleness(
		salaryBatchId,
		salaryBatch?.status === SALARY_BATCH_STATUS.PREVIEW
	);

	// Ref to track if component is mounted
	const isMountedRef = React.useRef(true);

	useEffect(() => {
		// Set mounted flag
		isMountedRef.current = true;

		return () => {
			// Cleanup: set mounted flag to false when component unmounts
			isMountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		const initSalaryBatch = async () => {
			if (!salaryBatchId) return;

			const loadedBatch = await pollSalaryBatch(salaryBatchId)
				.then(parseSalaryBatch)
				.catch(() => undefined);

			if (!isMountedRef.current || !loadedBatch) return;

			setSalaryBatch(loadedBatch);
			setConsultantIdList(loadedBatch.consultants.filter((c) => c.include).map((c) => c.id));

			// If navigating to a batch in INITIAL state, start polling
			if (loadedBatch.status === SALARY_BATCH_STATUS.INITIAL) {
				setIsPollingPreview(true);
				try {
					const previewBatch = await pollUntilPreviewReady(loadedBatch.id);

					// Only update state if component is still mounted
					if (!isMountedRef.current) return;

					setSalaryBatch(parseSalaryBatch(previewBatch));

					if (previewBatch.status === SALARY_BATCH_STATUS.PREVIEW) {
						toast.success(t("salary.previewReady"));
					}
				} catch (err) {
					// Silently ignore if component was unmounted
					if (err instanceof Error && err.message === "Component unmounted") {
						return;
					}
					// Error already handled in pollUntilPreviewReady
				} finally {
					// Only update state if component is still mounted
					if (isMountedRef.current) {
						setIsPollingPreview(false);
					}
				}
			}
		};

		initSalaryBatch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [salaryBatchId]);

	/**
	 * Creates a new salary batch preview.
	 */
	const handleCreatePreview = async (from: Date, to: Date, filter: SalaryBatchFilter) => {
		const newBatchRequest: NewSalaryBatchRequest = {
			from: from.toISOString(),
			to: to.toISOString(),
			...filter,
		};
		let data: SalaryBatch;
		try {
			data = parseSalaryBatch(await createSalaryBatch(newBatchRequest));
		} catch (e) {
			if ((e as ApiErrorResponse)?.error?.code === "NOT_FOUND") {
				toast.info(t("salary.submitFailure404"));
			} else {
				toast.error(t("salary.submitFailure"));
			}
			setSalaryBatch(undefined);
			return;
		}

		setSalaryBatch(data);

		// Navigate to the batch detail page immediately
		if (data?.id) {
			navigate(routes.SALARY_BATCH_DETAILS.replace(":salaryBatchId", data.id));
		}

		// If status is INITIAL, poll until it reaches PREVIEW or FAILED
		if (data?.status === SALARY_BATCH_STATUS.INITIAL) {
			setIsPollingPreview(true);
			try {
				const previewBatch = await pollUntilPreviewReady(data.id);

				// Only update state if component is still mounted
				if (!isMountedRef.current) return;

				setSalaryBatch(parseSalaryBatch(previewBatch));

				if (previewBatch.status === SALARY_BATCH_STATUS.PREVIEW) {
					toast.success(t("salary.previewReady"));
				}
			} catch (err) {
				// Silently ignore if component was unmounted
				if (err instanceof Error && err.message === "Component unmounted") {
					return;
				}
				// Error already handled in pollUntilPreviewReady
			} finally {
				// Only update state if component is still mounted
				if (isMountedRef.current) {
					setIsPollingPreview(false);
				}
			}
		}
	};

	const handleCheckBox = async (e: any, consultantId?: string) => {
		if (salaryBatchId && consultantId) {
			const isCheck = e.target.checked;
			const ids = [...consultantIdList];

			if (isCheck) ids.push(consultantId);
			else {
				const index = ids.indexOf(consultantId);
				if (index > -1) ids.splice(index, 1);
			}

			setConsultantIdList(ids);

			await updateSalaryBatchConsultants({ salaryBatchId, update: { consultantIds: ids } })
				.then((updated) => setSalaryBatch(parseSalaryBatch(updated)))
				.catch(() => undefined);
		}
	};

	const handleSelectAll = async (e: any) => {
		if (!salaryBatchId || !salaryBatch?.consultants) return;

		const allIds = salaryBatch.consultants.map((c) => c.id);
		const ids = e.target.checked ? allIds : [];

		setConsultantIdList(ids);

		await updateSalaryBatchConsultants({ salaryBatchId, update: { consultantIds: ids } })
			.then((updated) => setSalaryBatch(parseSalaryBatch(updated)))
			.catch(() => undefined);
	};

	/**
	 * Processes the salary batch that currently is in preview mode.
	 * Will either create and download export files (Hogia) or start invoking
	 * the external system directly via API (Fortnox).
	 */
	const processSalaries = async () => {
		setProcessing(true);

		let data: SalaryBatch | undefined;
		let error: ApiError | undefined;
		try {
			data = parseSalaryBatch(await processSalaryBatch(salaryBatch?.id || ""));
			setSalaryBatch(data);
		} catch (e) {
			error = (e as ApiErrorResponse)?.error;
		}

		if (data) {
			if (isHogiaExport) {
				setProcessing(false);
				toast.success(t("salaryBatch.success.hogia"));

				const salaryProcessModal: ConfirmModalProps = {
					title: "SALARY_PROCESS_MODAL_TITLE",
					text: "SALARY_PROCESS_MODAL_TEXT",
					acceptButtonText: "DOWNLOAD_FILE",
					rejectButtonText: "NOT_NOW_BUTTON",
					onReject: () => redirectToSalaryList(),
					onAccept: () => downloadFile(),
				};
				modalStore.newModal(salaryProcessModal);
			} else if (organisation?.salarySettings.exportType === "FORTNOX") {
				setProcessingSalaryBatch(data);
				try {
					const finalBatch = await pollUntilFinishedProcessing();

					// Only update state if component is still mounted
					if (isMountedRef.current) {
						setProcessingSalaryBatch(finalBatch);
					}
				} catch (err) {
					// Silently ignore if component was unmounted
					if (err instanceof Error && err.message === "Component unmounted") {
						return;
					}
					// Re-throw other errors
					throw err;
				}
			}
		} else {
			if (isHogiaExport) {
				toast.error(FEEDBACK_MESSAGE_TYPE.SOMETHING_WENT_WRONG);
			} else if (organisation?.salarySettings.exportType === "FORTNOX") {
				setProcessingError(error);
			}
		}
	};

	const downloadFile = async () => {
		if (salaryBatch?.status === "PROCESSED") {
			const url = await getExportFileUrl({
				type: salaryBatch.type,
				file: salaryBatch.exportFile!,
			}).catch(() => undefined);

			if (url) {
				// https://stackoverflow.com/a/59352848
				const downloadLink = document.createElement("a");
				downloadLink.href = /*apiStore.apiRoot +*/ url;
				downloadLink.download = /*apiStore.apiRoot +*/ url;
				downloadLink.click();
			}
		}
	};

	const handleDelete = async () => {
		const isPotentiallyDestructiveRollback = ["PROCESSED", "FAILED"].includes(
			salaryBatch?.status as string
		);

		if (
			salaryBatch &&
			(salaryBatch.status === "PREVIEW" ||
				(isPotentiallyDestructiveRollback && window.confirm(t("CONFIRM_ROLLBACK_SALARY_BATCH"))))
		) {
			const toastId = toast.loading(t("ROLLING_BACK_SALARY_BATCH"));

			await rollbackSalaryBatch(salaryBatch.id).catch(() => undefined);

			toast.success(t("ROLL_BACK_SALARY_BATCH_SUCCESS"), { id: toastId });

			navigate(routes.SALARY);
		}
	};

	/**
	 * Rebuilds the draft's preview in place (same batch id) after the
	 * underlying data changed. The recompute starts from scratch, so manual
	 * draft edits are confirmed away first.
	 */
	const handleRefresh = async () => {
		if (!salaryBatch) return;

		const hasManualEdits = salaryBatch.consultants.some((consultant) => !consultant.include);

		if (hasManualEdits && !window.confirm(t("batchRefresh.confirmDiscardEdits"))) return;

		setRefreshing(true);
		setIsPollingPreview(true);

		try {
			const initial = parseSalaryBatch(await refreshSalaryBatch(salaryBatch.id));
			setSalaryBatch(initial);

			const rebuilt = await pollUntilPreviewReady(initial.id);

			if (!isMountedRef.current) return;

			setSalaryBatch(parseSalaryBatch(rebuilt));
			setConsultantIdList(rebuilt.consultants.filter((c) => c.include).map((c) => c.id));
			toast.success(t("batchRefresh.success"));
		} catch (err) {
			if (err instanceof Error && err.message === "Component unmounted") return;

			// pollUntilPreviewReady toasts FAILED previews itself
			if (!(err instanceof Error) || err.message !== "Preview failed")
				toast.error(t("batchRefresh.failed"));
		} finally {
			if (isMountedRef.current) {
				setIsPollingPreview(false);
				setRefreshing(false);
			}
		}
	};

	const redirectToSalaryList = () => {
		navigate(routes.SALARY);
	};

	const showSalaryBreakdown = (consultant: ConsultantSalary) => {
		setShowConsultantDetails(consultant);
	};

	async function pollUntilPreviewReady(batchId: string): Promise<SalaryBatch> {
		await wait(4000); // Poll every 4 seconds

		// Check if component is still mounted before continuing
		if (!isMountedRef.current) {
			throw new Error("Component unmounted");
		}

		const pollResult = await pollSalaryBatch(batchId);

		if (pollResult.status === SALARY_BATCH_STATUS.INITIAL) {
			// Still creating, continue polling
			return await pollUntilPreviewReady(batchId);
		} else if (pollResult.status === SALARY_BATCH_STATUS.PREVIEW) {
			// Successfully created
			return pollResult;
		} else if (pollResult.status === SALARY_BATCH_STATUS.FAILED) {
			// Creation failed
			const previewError = pollResult.errors.find((e) => e.code === "PREVIEW_ERROR");
			const errorMessage =
				previewError?.previewError?.detail ||
				previewError?.previewError?.title ||
				t("salary.previewFailed");
			toast.error(errorMessage);
			throw new Error("Preview failed");
		}

		return pollResult;
	}

	async function pollUntilFinishedProcessing(): Promise<SalaryBatch> {
		await wait();

		// Check if component is still mounted before continuing
		if (!isMountedRef.current) {
			throw new Error("Component unmounted");
		}

		if (!salaryBatch?.id) {
			return pollUntilFinishedProcessing();
		}

		const pollResult = await pollSalaryBatch(salaryBatch.id);

		if (pollResult.status === "PROCESSING") {
			setProcessingSalaryBatch(pollResult);
			return await pollUntilFinishedProcessing();
		} else {
			return pollResult;
		}
	}

	function handleCloseProcessingModal() {
		if (showProcessingResult) {
			setShowProcessingResult(false);
		} else {
			navigate(routes.SALARY);
		}
	}

	return (
		<>
			<Breadcrumbs
				items={salaryBatchId ? SalaryBatchDetailsBreadcrumbs : SalaryBatchNewBreadcrumbs}
			/>
			<div className="p-4 px-6">
				<SalaryPreviewWrapper>
					<SalaryBatchForm salaryBatch={salaryBatch} onSubmit={handleCreatePreview} />

					<SalaryPreviewContainer>
						{!!salaryBatch?.consultants?.length ? (
							<React.Fragment>
								{isLoadingBatch || isPollingPreview ? (
									<LoadingIndicator
										showText={isPollingPreview ? t("salary.creatingPreview") : undefined}
									/>
								) : (
									<>
										<BatchStalenessBanner
											staleness={staleness}
											onRefresh={handleRefresh}
											isRefreshing={isRefreshing}
										/>
										<BatchWarningSummary
											warnings={salaryBatch.warnings}
											errors={salaryBatch.errors}
											issueNamespace="salaryBatch"
											period={{ start: salaryBatch.from, end: salaryBatch.to }}
										/>

										<TableLayout selectable={isEditable}>
											<table>
												<thead>
													<tr>
														{isEditable && (
															<th>
																<input
																	onChange={handleSelectAll}
																	checked={
																		consultantIdList.length ===
																		(salaryBatch?.consultants || []).length
																	}
																	type="checkbox"
																/>
															</th>
														)}
														<th>{t("EMPLOYEE_NO")}</th>
														<th>{t("CONSULTANTS")}</th>
														<th>{t("SALARY_FOR_PERIOD")}</th>
													</tr>
												</thead>
												<tbody>
													{(salaryBatch?.consultants || [])
														.filter((c) => isEditable || c.include)
														.map((consultant, index) => {
															return (
																<tr key={index}>
																	{isEditable && (
																		<td>
																			<input
																				onChange={(e: any) => handleCheckBox(e, consultant?.id)}
																				defaultChecked={
																					consultantIdList.find((item) => item === consultant.id)
																						? true
																						: false
																				}
																				type="checkbox"
																			/>
																		</td>
																	)}
																	<td
																		onClick={() => showSalaryBreakdown(consultant)}
																		className="hover:underline"
																	>
																		{consultant.profile?.employeeNumber}
																	</td>
																	<td
																		data-contact={consultant.id}
																		onClick={() => showSalaryBreakdown(consultant)}
																		className="hover:underline"
																	>
																		{consultant.firstName} {consultant.lastName}
																	</td>
																	<td
																		onClick={() => showSalaryBreakdown(consultant)}
																		className="hover:underline"
																	>
																		{formatCurrencyString(consultant.salary?.grossSalary, {
																			currency: consultant?.profile?.employment?.salary[0].currency,
																		})}
																	</td>
																</tr>
															);
														})}
												</tbody>
											</table>
										</TableLayout>
									</>
								)}
							</React.Fragment>
						) : (
							<React.Fragment>
								{isLoadingBatch || isPollingPreview ? (
									<LoadingIndicator
										showText={isPollingPreview ? t("salary.creatingPreview") : undefined}
									/>
								) : (
									<>
										{salaryBatch && (
											<>
												<BatchStalenessBanner
													staleness={staleness}
													onRefresh={handleRefresh}
													isRefreshing={isRefreshing}
												/>
												<BatchWarningSummary
													warnings={salaryBatch.warnings}
													errors={salaryBatch.errors}
													issueNamespace="salaryBatch"
													period={{ start: salaryBatch.from, end: salaryBatch.to }}
												/>
											</>
										)}
										<EmptyState margin="1rem 2rem" label="NO_SALARY_PREVIEW" />
									</>
								)}
							</React.Fragment>
						)}

						{salaryBatch && salaryBatch.status !== "INITIAL" && (
							<TotalSalaryPreview>
								{(salaryBatch?.consultants || []).length > 0 ? (
									<div>
										<AppText weight="bold" size="xms">
											{t("TOTAL")}: {formatCurrencyString(salaryBatch?.totalGrossPay || 0)}
										</AppText>
										<AppText size="s">
											{t("NUM_CONSULTANTS")}:{" "}
											{
												(salaryBatch?.consultants || []).filter((consultant) => consultant.include)
													.length
											}
										</AppText>
									</div>
								) : (
									<div />
								)}
								<div style={{ display: "flex" }}>
									{(salaryBatch?.consultants || []).length > 0 && (
										<a
											target="_blank"
											rel="noopener noreferrer"
											href={`/salary/salary-journal/${salaryBatch?.id || salaryBatchId}`}
										>
											<Button
												width={"150px"}
												textColor={"buttonTextGray"}
												margin={"0 1rem"}
												backgroundColor={"buttonGray"}
											>
												{t("SALARY_JOURNAL")}
											</Button>
										</a>
									)}
									{isEditable && (salaryBatch?.consultants || []).length > 0 && (
										<Button width={"116px"} onClick={() => processSalaries()}>
											{t("RUN_SALARY")}
										</Button>
									)}

									{salaryBatch?.status === "PROCESSED" && salaryBatch.exportFile && (
										<DownloadButton width={"156px"} onClick={() => downloadFile()}>
											{t("DOWNLOAD_EXPORT_FILE")}
										</DownloadButton>
									)}

									{(salaryBatch?.status === "PREVIEW" || salaryBatch?.status === "FAILED") && (
										<ButtonNew variant="neutral" onClick={handleRefresh} disabled={isRefreshing}>
											<RefreshCw size={16} className={isRefreshing ? "animate-spin" : undefined} />
											{t("batchRefresh.button")}
										</ButtonNew>
									)}
									{(salaryBatch?.status === "PROCESSED" ||
										salaryBatch?.status === "PREVIEW" ||
										salaryBatch?.status === "FAILED") && (
										<Button backgroundColor="danger" onClick={handleDelete} margin="0 1rem">
											{t(salaryBatch?.status === "PREVIEW" ? "CANCEL" : "invoiceBatch.cancel")}
										</Button>
									)}
								</div>
							</TotalSalaryPreview>
						)}

						{showConsultantDetails && (
							<SalaryDetailsModal
								consultant={showConsultantDetails}
								onClose={() => setShowConsultantDetails(undefined)}
								periodFrom={salaryBatch?.from}
								periodTo={salaryBatch?.to}
							/>
						)}
						{(isProcessing || showProcessingResult) && (
							<SalaryBatchProcessingModal
								salaryBatch={processingSalaryBatch}
								error={processingError}
								onClose={handleCloseProcessingModal}
							/>
						)}
					</SalaryPreviewContainer>
				</SalaryPreviewWrapper>
			</div>
		</>
	);
};

export default SalaryBatchPage;

const SalaryPreviewWrapper = styled.div`
	display: flex;
`;

const SalaryPreviewContainer = styled.div`
	margin-left: 1rem;
	width: 100%;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	position: relative;
	padding-bottom: 5rem;
`;

const TotalSalaryPreview = styled.div`
	display: flex;
	justify-content: space-between;
	background-color: white;
	padding: 1rem 3rem;
	border-top-left-radius: 25px;
	border-top-right-radius: 25px;
	box-shadow: 0px -1px 10px rgba(0, 0, 0, 0.1);
	position: fixed;
	bottom: 0;

	/* Could not come up with better solution for width :/ /JS */
	width: calc(100vw - ${SIDE_MENU_WIDTH}px - 290px);
`;
